/*
  Scroll reveal.

  Adds the hidden state ONLY after confirming IntersectionObserver exists, so a
  browser without it — or a failure loading this file — leaves every element
  visible rather than blank. That ordering is the whole safety argument: the
  class that hides things is applied by the same code that can un-hide them.

  Targets are selected here rather than by hand-editing every section, so the
  motion is consistent across pages and a new section inherits it by using the
  same class names the rest of the theme already uses.
*/
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;

  var TARGETS = [
    '.fl-head',
    '.line-card',
    '.proof__stat',
    '.fl-table__card',
    '.fl-archive__card',
    '.fl-404__route',
    '.fl-xsell__item',
    '.fl-delivery__item',
    '.fl-services__card',
    '.fl-press__item',
    '.fl-timeline__entry',
    '.fl-quote__inner',
    /* NOT .fl-wall__slip: the wall scrolls continuously, so its slips move in
       and out of the viewport forever. A reveal that hides until intersection
       leaves every off-screen slip at opacity 0 and the two systems fight.
       Anything with its own animation opts out of this one. */
    '.fl-ptitle',
    '.card-wrapper',
    '.fl-recipe__card',
    '.fl-recipes-index__use',
    '.fl-shop__group-head',
    '[data-fl-reveal]'
  ].join(',');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init() {
    document.documentElement.classList.add('fl-motion');

    var nodes = Array.prototype.slice.call(document.querySelectorAll(TARGETS));

    // .fl-wipe is a separate opt-in used by the timeline spine and rules: it
    // scales from zero rather than fading, so it must NOT also get .fl-reveal.
    // It still needs is-in from the observer, though — without that it stays at
    // scaleY(0) forever, which is what left the timeline spines invisible.
    var wipes = Array.prototype.slice.call(document.querySelectorAll('.fl-wipe'));

    if (!nodes.length && !wipes.length) return;

    // Reduced motion: mark everything shown immediately. The stylesheet already
    // neutralises the transform, but this keeps the DOM in its final state so
    // nothing depends on the observer firing.
    if (reduce.matches) {
      nodes.forEach(function (n) {
        n.classList.add('fl-reveal', 'is-in');
      });
      wipes.forEach(function (n) {
        n.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.06 }
    );

    nodes.forEach(function (node) {
      // Anything already on screen at load is shown without animating, so the
      // first paint is never a page of invisible elements waiting on a scroll.
      var box = node.getBoundingClientRect();
      var onScreenNow = box.top < window.innerHeight * 0.92 && box.bottom > 0;

      node.classList.add('fl-reveal');

      if (onScreenNow) {
        node.classList.add('is-in');
        return;
      }

      // Stagger within a row: siblings matching the same selector step by one.
      var parent = node.parentElement;
      if (parent) {
        var sibs = Array.prototype.filter.call(parent.children, function (c) {
          return c.matches && c.matches(TARGETS);
        });
        var i = sibs.indexOf(node);
        if (i > 0) node.style.setProperty('--fl-i', String(Math.min(i, 6)));
      }

      io.observe(node);
    });

    wipes.forEach(function (node) {
      var box = node.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.92 && box.bottom > 0) {
        node.classList.add('is-in');
        return;
      }
      io.observe(node);
    });

    /* FAIL-SAFE. This reveal hides content until the observer says otherwise,
       so anything that stops the observer firing leaves a blank page. Two real
       cases have already done it: an anchor jump (/collections/...#shop) moves
       the page after init, and an element that is display:none or [hidden] when
       the observer first evaluates reports no intersection and may never be
       re-evaluated once shown.

       So after load, and again shortly after, sweep anything still hidden that
       is actually within the viewport and show it. Content being visible always
       beats content animating correctly. */
    var sweep = function () {
      var vh = window.innerHeight;
      document.querySelectorAll('.fl-reveal:not(.is-in), .fl-wipe:not(.is-in)').forEach(function (n) {
        var r = n.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0 && r.height > 0) n.classList.add('is-in');
      });
    };

    window.addEventListener('load', function () {
      sweep();
      window.setTimeout(sweep, 400);
    });
    window.addEventListener('hashchange', function () {
      window.setTimeout(sweep, 60);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
